import type { NormalizedInput } from '../game/models';
import type { TouchMode } from '../storage/storage';

export type KeyboardPreset = 'faithful' | 'standard';
type InputSource = 'keyboard' | 'gamepad' | 'touch';

interface LeverState {
  element: HTMLButtonElement;
  pointerId: number | null;
  value: number;
}

const FIRE_KEYS = new Set(['Space', 'Enter']);

export class InputManager {
  private readonly keys = new Set<string>();
  private readonly leftLever: LeverState;
  private readonly rightLever: LeverState;
  private fireHeld = false;
  private fireEdge = false;
  private source: InputSource = 'keyboard';
  private preset: KeyboardPreset = 'faithful';
  private touchMode: TouchMode = 'auto';
  private gamepadPauseHeld = false;

  public constructor(
    leftLeverElement: HTMLButtonElement,
    rightLeverElement: HTMLButtonElement,
    private readonly fireElement: HTMLButtonElement,
    private readonly isGameActive: () => boolean,
    private readonly requestPause: () => void,
    private readonly requestSystemToggle: () => void,
  ) {
    this.leftLever = { element: leftLeverElement, pointerId: null, value: 0 };
    this.rightLever = { element: rightLeverElement, pointerId: null, value: 0 };
  }

  public mount(): void {
    window.addEventListener('keydown', this.handleKeyDown, { passive: false });
    window.addEventListener('keyup', this.handleKeyUp, { passive: false });
    window.addEventListener('blur', this.handleBlur);
    window.addEventListener('gamepaddisconnected', this.handleGamepadDisconnected);
    window.addEventListener('pointerdown', this.handleGlobalPointerDown, { capture: true });
    this.mountLever(this.leftLever);
    this.mountLever(this.rightLever);
    this.fireElement.addEventListener('pointerdown', this.handleFireDown);
    this.fireElement.addEventListener('pointerup', this.handleFireUp);
    this.fireElement.addEventListener('pointercancel', this.handleFireUp);
    if (window.matchMedia('(any-pointer: coarse)').matches) this.showTouchControls();
  }

  public destroy(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.handleBlur);
    window.removeEventListener('gamepaddisconnected', this.handleGamepadDisconnected);
    window.removeEventListener('pointerdown', this.handleGlobalPointerDown, { capture: true });
    this.unmountLever(this.leftLever);
    this.unmountLever(this.rightLever);
    this.fireElement.removeEventListener('pointerdown', this.handleFireDown);
    this.fireElement.removeEventListener('pointerup', this.handleFireUp);
    this.fireElement.removeEventListener('pointercancel', this.handleFireUp);
  }

  public setPreset(preset: KeyboardPreset): void {
    this.preset = preset;
    this.keys.clear();
  }

  public setTouchMode(mode: TouchMode): void {
    this.touchMode = mode;
    document.body.dataset.touchMode = mode;
    if (mode === 'always') document.body.dataset.touchVisible = 'true';
    else if (mode === 'never') delete document.body.dataset.touchVisible;
    else if (window.matchMedia('(any-pointer: coarse)').matches) document.body.dataset.touchVisible = 'true';
    else delete document.body.dataset.touchVisible;
  }

  public sample(): NormalizedInput {
    const gamepad = this.sampleGamepad();
    let treadInput: Pick<NormalizedInput, 'leftTread' | 'rightTread'>;
    if (gamepad) treadInput = gamepad;
    else if (this.source === 'touch') {
      treadInput = { leftTread: this.leftLever.value, rightTread: this.rightLever.value };
    } else treadInput = this.sampleKeyboard();

    const result = {
      ...treadInput,
      firePressed: this.fireEdge,
    };
    this.fireEdge = false;
    return result;
  }

  public clear(): void {
    this.keys.clear();
    this.leftLever.value = 0;
    this.rightLever.value = 0;
    this.leftLever.pointerId = null;
    this.rightLever.pointerId = null;
    this.fireHeld = false;
    this.fireEdge = false;
    this.gamepadPauseHeld = false;
    this.updateLeverVisual(this.leftLever);
    this.updateLeverVisual(this.rightLever);
  }

  private sampleKeyboard(): Pick<NormalizedInput, 'leftTread' | 'rightTread'> {
    if (this.preset === 'standard') {
      const forward = this.axis('KeyS', 'KeyW') || this.axis('ArrowDown', 'ArrowUp');
      const turn = this.axis('KeyA', 'KeyD') || this.axis('ArrowLeft', 'ArrowRight');
      return {
        leftTread: Math.max(-1, Math.min(1, forward + turn)),
        rightTread: Math.max(-1, Math.min(1, forward - turn)),
      };
    }
    return {
      leftTread: this.axis('KeyS', 'KeyW'),
      rightTread: this.axis('ArrowDown', 'ArrowUp'),
    };
  }

  private sampleGamepad(): Pick<NormalizedInput, 'leftTread' | 'rightTread'> | null {
    const gamepads = navigator.getGamepads?.() ?? [];
    const gamepad = [...gamepads].find((candidate) => candidate?.connected);
    if (!gamepad) return null;
    const left = this.normalizeGamepadAxis(-(gamepad.axes[1] ?? 0));
    const right = this.normalizeGamepadAxis(-(gamepad.axes[3] ?? 0));
    const active = Math.abs(left) > 0 || Math.abs(right) > 0 || gamepad.buttons.some((button) => button.pressed);
    if (!active && this.source !== 'gamepad') return null;
    if (active) {
      this.source = 'gamepad';
      document.body.dataset.inputSource = 'gamepad';
      if (this.touchMode === 'auto') delete document.body.dataset.touchVisible;
    }
    const fire = Boolean(
      gamepad.buttons[0]?.pressed ||
      gamepad.buttons[1]?.pressed ||
      gamepad.buttons[2]?.pressed ||
      gamepad.buttons[3]?.pressed ||
      gamepad.buttons[7]?.pressed
    );
    if (fire && !this.fireHeld) this.fireEdge = true;
    this.fireHeld = fire;
    const pause = Boolean(gamepad.buttons[9]?.pressed);
    if (pause && !this.gamepadPauseHeld) this.requestSystemToggle();
    this.gamepadPauseHeld = pause;
    return { leftTread: left, rightTread: right };
  }

  private normalizeGamepadAxis(value: number): number {
    if (!Number.isFinite(value) || Math.abs(value) < 0.25) return 0;
    if (Math.abs(value) > 0.9) return Math.sign(value);
    return Math.max(-1, Math.min(1, value));
  }

  private axis(negativeCode: string, positiveCode: string): number {
    return Number(this.keys.has(positiveCode)) - Number(this.keys.has(negativeCode));
  }

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'Escape' || event.code === 'KeyP') {
      if (!event.repeat && this.isGameActive()) this.requestSystemToggle();
      return;
    }
    if (this.shouldDeferToUi(event)) return;
    const gameKey = this.isGameKey(event.code);
    if (gameKey && this.isGameActive()) event.preventDefault();
    if (!gameKey) return;
    this.source = 'keyboard';
    document.body.dataset.inputSource = 'keyboard';
    if (this.touchMode === 'auto') delete document.body.dataset.touchVisible;
    this.keys.add(event.code);
    if (FIRE_KEYS.has(event.code) && !event.repeat) this.fireEdge = true;
  };

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    if (!this.shouldDeferToUi(event) && this.isGameKey(event.code) && this.isGameActive()) event.preventDefault();
    this.keys.delete(event.code);
    if (FIRE_KEYS.has(event.code)) this.fireHeld = false;
  };

  private readonly handleBlur = (): void => {
    this.clear();
    if (this.isGameActive()) this.requestPause();
  };

  private readonly handleGamepadDisconnected = (): void => {
    if (this.source === 'gamepad' && this.isGameActive()) {
      this.clear();
      this.requestPause();
    }
  };

  private readonly handleGlobalPointerDown = (event: PointerEvent): void => {
    if (event.pointerType === 'touch' && this.touchMode === 'auto') this.showTouchControls();
  };

  private isGameKey(code: string): boolean {
    return [
      'KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
      'Space', 'Enter',
    ].includes(code);
  }

  private shouldDeferToUi(event: KeyboardEvent): boolean {
    const target = event.target;
    if (target instanceof Element && target.closest('button, input, select, textarea, a[href], [contenteditable="true"]')) {
      return true;
    }
    return document.querySelector('dialog[open]') !== null;
  }

  private mountLever(lever: LeverState): void {
    lever.element.addEventListener('pointerdown', this.handleLeverDown);
    lever.element.addEventListener('pointermove', this.handleLeverMove);
    lever.element.addEventListener('pointerup', this.handleLeverUp);
    lever.element.addEventListener('pointercancel', this.handleLeverUp);
  }

  private unmountLever(lever: LeverState): void {
    lever.element.removeEventListener('pointerdown', this.handleLeverDown);
    lever.element.removeEventListener('pointermove', this.handleLeverMove);
    lever.element.removeEventListener('pointerup', this.handleLeverUp);
    lever.element.removeEventListener('pointercancel', this.handleLeverUp);
  }

  private readonly handleLeverDown = (event: PointerEvent): void => {
    const lever = this.leverForElement(event.currentTarget);
    if (!lever || lever.pointerId !== null) return;
    event.preventDefault();
    lever.pointerId = event.pointerId;
    try {
      lever.element.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic test events do not create an active browser pointer.
    }
    this.source = 'touch';
    this.showTouchControls();
    this.updateLeverFromPointer(lever, event.clientY);
  };

  private readonly handleLeverMove = (event: PointerEvent): void => {
    const lever = this.leverForElement(event.currentTarget);
    if (!lever || lever.pointerId !== event.pointerId) return;
    event.preventDefault();
    this.updateLeverFromPointer(lever, event.clientY);
  };

  private readonly handleLeverUp = (event: PointerEvent): void => {
    const lever = this.leverForElement(event.currentTarget);
    if (!lever || lever.pointerId !== event.pointerId) return;
    lever.pointerId = null;
    lever.value = 0;
    this.updateLeverVisual(lever);
  };

  private readonly handleFireDown = (event: PointerEvent): void => {
    event.preventDefault();
    this.source = 'touch';
    this.showTouchControls();
    if (!this.fireHeld) this.fireEdge = true;
    this.fireHeld = true;
    try {
      this.fireElement.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic test events do not create an active browser pointer.
    }
    this.fireElement.classList.add('touch-fire--active');
  };

  private readonly handleFireUp = (): void => {
    this.fireHeld = false;
    this.fireElement.classList.remove('touch-fire--active');
  };

  private leverForElement(target: EventTarget | null): LeverState | null {
    if (target === this.leftLever.element) return this.leftLever;
    if (target === this.rightLever.element) return this.rightLever;
    return null;
  }

  private updateLeverFromPointer(lever: LeverState, clientY: number): void {
    const bounds = lever.element.getBoundingClientRect();
    const center = bounds.top + bounds.height / 2;
    const halfRange = Math.max(1, bounds.height * 0.34);
    lever.value = Math.max(-1, Math.min(1, (center - clientY) / halfRange));
    if (Math.abs(lever.value) < 0.18) lever.value = 0;
    this.updateLeverVisual(lever);
  }

  private updateLeverVisual(lever: LeverState): void {
    lever.element.style.setProperty('--lever-value', String(lever.value));
    lever.element.setAttribute('aria-valuetext', lever.value > 0.18 ? 'forward' : lever.value < -0.18 ? 'reverse' : 'neutral');
  }

  private showTouchControls(): void {
    if (this.touchMode === 'never') return;
    document.body.dataset.touchVisible = 'true';
    document.body.dataset.inputSource = 'touch';
  }
}
